/**
 * @fileoverview HubSpot CRM Integration Configuration Component
 * Allows users to connect HubSpot, configure field mappings,
 * and manage sync settings.
 */

import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Check,
  Loader2,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Trash2,
  Settings2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

// HubSpot OAuth configuration
const HUBSPOT_CLIENT_ID = import.meta.env.VITE_HUBSPOT_CLIENT_ID || '';
const HUBSPOT_REDIRECT_URI = `${window.location.origin}/settings/integrations/hubspot/callback`;
const HUBSPOT_SCOPES = [
  'crm.objects.contacts.read',
  'crm.objects.contacts.write',
  'crm.objects.deals.read',
  'crm.objects.deals.write',
  'crm.schemas.contacts.read',
].join(' ');

interface HubSpotIntegration {
  id: string;
  status: 'active' | 'disconnected' | 'error';
  config: {
    portal_id?: string;
    portal_name?: string;
    field_mappings?: Record<string, string>;
    auto_create_contacts?: boolean;
    log_all_calls?: boolean;
  };
  last_error?: string;
  last_sync_at?: string;
  created_at: string;
}

export function HubSpotConfig() {
  const { activeTenantId } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [autoCreateContacts, setAutoCreateContacts] = useState(true);
  const [logAllCalls, setLogAllCalls] = useState(true);

  // Fetch current HubSpot integration status
  const { data: integration, isLoading: isLoadingIntegration } = useQuery({
    queryKey: ['hubspot-integration', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return null;

      const { data, error } = await supabase
        .from('calie_integrations')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .eq('provider', 'hubspot')
        .maybeSingle();

      if (error) throw error;
      return data as HubSpotIntegration | null;
    },
    enabled: !!activeTenantId,
  });

  // Initialize settings from integration config
  useEffect(() => {
    if (integration?.config) {
      setAutoCreateContacts(integration.config.auto_create_contacts ?? true);
      setLogAllCalls(integration.config.log_all_calls ?? true);
    }
  }, [integration]);

  // Save settings
  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      if (!activeTenantId || !integration) throw new Error('No integration');

      const { error } = await supabase
        .from('calie_integrations')
        .update({
          config: {
            ...integration.config,
            auto_create_contacts: autoCreateContacts,
            log_all_calls: logAllCalls,
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', integration.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubspot-integration', activeTenantId] });
      toast({
        title: 'Settings saved',
        description: 'Your HubSpot integration settings have been updated.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error saving settings',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Disconnect HubSpot
  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!activeTenantId) throw new Error('No tenant');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('Calie_hubspot/disconnect', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { tenantId: activeTenantId },
      });

      if (response.error) throw response.error;
      if (!response.data?.success) throw new Error(response.data?.error || 'Disconnect failed');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubspot-integration', activeTenantId] });
      toast({
        title: 'HubSpot disconnected',
        description: 'Your HubSpot integration has been removed.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error disconnecting',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Refresh token
  const refreshTokenMutation = useMutation({
    mutationFn: async () => {
      if (!activeTenantId) throw new Error('No tenant');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('Calie_hubspot/refresh', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { tenantId: activeTenantId },
      });

      if (response.error) throw response.error;
      if (!response.data?.success) throw new Error(response.data?.error || 'Refresh failed');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubspot-integration', activeTenantId] });
      toast({
        title: 'Token refreshed',
        description: 'Your HubSpot connection has been refreshed.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Refresh failed',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Handle HubSpot OAuth
  const handleConnect = () => {
    const state = btoa(JSON.stringify({ tenantId: activeTenantId }));
    const authUrl = `https://app.hubspot.com/oauth/authorize?client_id=${HUBSPOT_CLIENT_ID}&scope=${encodeURIComponent(HUBSPOT_SCOPES)}&redirect_uri=${encodeURIComponent(HUBSPOT_REDIRECT_URI)}&state=${state}`;
    window.location.href = authUrl;
  };

  if (isLoadingIntegration) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <Building2 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <CardTitle>HubSpot CRM</CardTitle>
              <CardDescription>
                Sync contacts and log calls to HubSpot
              </CardDescription>
            </div>
          </div>
          {integration?.status === 'active' && (
            <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
              <Check className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          )}
          {integration?.status === 'error' && (
            <Badge variant="destructive">
              <AlertCircle className="h-3 w-3 mr-1" />
              Error
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Alert */}
        {integration?.status === 'error' && integration.last_error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {integration.last_error}. Please reconnect your HubSpot account.
            </AlertDescription>
          </Alert>
        )}

        {/* Not Connected State */}
        {!integration && (
          <div className="text-center py-6">
            <p className="text-muted-foreground mb-4">
              Connect your HubSpot CRM to automatically sync contacts and log call activities.
            </p>
            <Button onClick={handleConnect} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              Connect HubSpot
            </Button>
          </div>
        )}

        {/* Connected State */}
        {integration?.status === 'active' && (
          <>
            {/* Portal Info */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Connected Portal</p>
                  <p className="font-medium">{integration.config.portal_name || 'Unknown'}</p>
                  <p className="text-xs text-muted-foreground">
                    Portal ID: {integration.config.portal_id}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refreshTokenMutation.mutate()}
                  disabled={refreshTokenMutation.isPending}
                >
                  <RefreshCw className={`h-4 w-4 ${refreshTokenMutation.isPending ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Sync Settings */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Settings2 className="h-4 w-4" />
                <h4 className="font-medium">Sync Settings</h4>
              </div>

              {/* Auto Create Contacts */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label>Auto-create Contacts</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically create new contacts in HubSpot when receiving calls from unknown numbers
                  </p>
                </div>
                <Switch
                  checked={autoCreateContacts}
                  onCheckedChange={setAutoCreateContacts}
                />
              </div>

              {/* Log All Calls */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label>Log All Calls</Label>
                  <p className="text-sm text-muted-foreground">
                    Create a call activity in HubSpot for every call (including missed calls)
                  </p>
                </div>
                <Switch
                  checked={logAllCalls}
                  onCheckedChange={setLogAllCalls}
                />
              </div>
            </div>

            {/* Last Sync Info */}
            {integration.last_sync_at && (
              <p className="text-xs text-muted-foreground">
                Last synced: {new Date(integration.last_sync_at).toLocaleString()}
              </p>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-4 border-t">
              <Button
                onClick={() => saveSettingsMutation.mutate()}
                disabled={saveSettingsMutation.isPending}
              >
                {saveSettingsMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Save Settings
              </Button>

              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => disconnectMutation.mutate()}
                disabled={disconnectMutation.isPending}
              >
                {disconnectMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Disconnect
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
